import type {
  BotOptionSpec,
  UnsafeHoursWindow,
} from "features/adminUsers/adminUsers";
import { readOption } from "utils/additionalOptions";

// Only an explicit "off" lets a run through its unsafe hours - the same reading
// as run_clock.unsafe_hours_prevented, so the warning never disagrees with the
// bot that refuses the launch.
const FALSE_VALUES = ["false", "0", "no", "off"];
const WEEKDAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

/** The unsafe hours a job is held to, or null when its bot declares none or the
 *  job lets it run through them (PREVENT_UNSAFE_HOURS=False). */
export function blockedUnsafeHours(
  specs: BotOptionSpec[],
  rawOptions: string | null | undefined,
): UnsafeHoursWindow | null {
  const spec = specs.find((s) => s.unsafe_hours);
  if (!spec?.unsafe_hours) return null;
  const stored = (readOption(rawOptions, spec.key) ?? "")
    .trim()
    .replace(/^["']|["']$/g, "")
    .toLowerCase();
  return FALSE_VALUES.includes(stored) ? null : spec.unsafe_hours;
}

function minutesOf(hhmm: string): number {
  const [hours, minutes] = hhmm.split(":").map(Number);
  return hours * 60 + minutes;
}

/** Weekday ("MON") and minute of the day of an instant on the window's clock. */
function wallClock(instant: Date, timeZone: string): { day: string; minute: number } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(instant);
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return {
    day: part("weekday").toUpperCase(),
    minute: Number(part("hour")) * 60 + Number(part("minute")),
  };
}

/** Whether a run starting at this instant falls inside the window. A window
 *  opens on each of its days at `start` and closes at the next `end` - the same
 *  day, or the next morning when `end` is not after `start`. */
export function isInsideUnsafeHours(
  window: UnsafeHoursWindow,
  instant: Date,
): boolean {
  const { day, minute } = wallClock(instant, window.timezone);
  const start = minutesOf(window.start);
  const end = minutesOf(window.end);
  if (end > start) {
    return window.days.includes(day) && minute >= start && minute < end;
  }
  const yesterday = WEEKDAYS[(WEEKDAYS.indexOf(day) + 6) % 7];
  return (
    (window.days.includes(day) && minute >= start) ||
    (window.days.includes(yesterday) && minute < end)
  );
}
